import { PRODUCT_NAME } from '../../constants/brand';
import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  TextField, 
  Button,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { 
  Send as SendIcon, 
  Description as DocumentationIcon,
  Help as HelpIcon 
} from '@mui/icons-material';
import Select from 'react-select';
import Grid from '@mui/material/Grid2';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import useAlert from '../../alert/useAlert';
import { supabase } from '../../supabaseClient';

const Help = () => {
  const { userDetails } = useSelector((state) => ({userDetails: state.globalState.userDetails}), shallowEqual);
  const dispatch = useDispatch(); 
  const { setAlert } = useAlert();

  // Contact form state
  const [formData, setFormData] = useState({
    subject: '',
    message: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Documentation dialog state
  const [docDialogOpen, setDocDialogOpen] = useState(false);

  // Handle form input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      errors.subject = 'Subject must be at least 5 characters';
    }
    
    if (!formData.message.trim()) {
      errors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!userDetails?.id) {
      setAlert('User not authenticated. Please log in again.', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      const emailResponse = await fetch(`${process.env.REACT_APP_MAIN_BACKEND_URL}/send-contact-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: userDetails.name || 'Unknown User',
          email: userDetails.email,
          subject: formData.subject.trim(),
          message: formData.message.trim(),
        }),
      });

      if (!emailResponse.ok) {
        console.error('Failed to send email');
        setAlert('Failed to send your inquiry. Please try again later.', 'error');
      } else {
        setAlert('Your inquiry has been submitted successfully! We\'ll get back to you soon.', 'success');
      }

      // Reset form
      setFormData({
        subject: '',
        message: ''
      });
      
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setAlert('Failed to submit inquiry. Please try again later.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Documentation content (placeholder)
  const documentationContent = `
# ${PRODUCT_NAME} Documentation

## Getting Started
Welcome to the ${PRODUCT_NAME} application! This comprehensive tool helps healthcare professionals streamline their documentation process.

## Key Features

### 1. Voice Transcription
- Real-time speech-to-text conversion
- Support for multiple languages
- Medical terminology recognition
- Background noise filtering

### 2. Report Generation
- AI-powered medical report creation
- Customizable templates
- Multi-language output support
- Professional formatting

### 3. Patient Management
- Secure patient data storage
- HIPAA-compliant encryption
- Easy consultation history access
- Team collaboration features

### 4. Medical Coding
- Automated ICD code suggestions
- Code validation and verification
- Integration with billing systems
- Compliance tracking

## Quick Start Guide

1. **Create a New Consultation**
   - Navigate to the main dashboard
   - Click "New Consultation"
   - Select patient or create new patient record

2. **Start Recording**
   - Choose your audio input source
   - Click the record button
   - Speak clearly for best transcription results

3. **Generate Reports**
   - Review transcribed content
   - Select appropriate template
   - Generate AI-powered medical report
   - Review and edit as needed

4. **Save and Share**
   - Save consultation notes
   - Generate final reports
   - Share with team members if needed

## System Requirements
- Modern web browser (Chrome, Firefox, Safari, Edge)
- Microphone access for voice recording
- Stable internet connection
- Minimum 4GB RAM recommended

## Support
If you need additional help, please contact our support team through the contact form in this help section.

## Privacy and Security
Your data is protected with enterprise-grade encryption and follows all HIPAA compliance requirements.
  `;

  return (
    <div>
      <Typography variant="h5" sx={{ marginBottom: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <HelpIcon />
        Help and Support
      </Typography>
      
      <Grid container spacing={3}>
        {/* Contact Form Section */}
        <Grid size={12}>
          <Paper 
            elevation={2} 
            sx={{ 
              backgroundColor: '#FFFFFF', 
              borderRadius: '12px', 
              padding: '32px',
              height: 'fit-content'
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2, color: '#29C0BB' }}>
              Contact Form
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 3, color: '#666' }}>
              Have a question or need assistance? Send us a message and we'll get back to you promptly.
            </Typography>
            
            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                label="Subject"
                value={formData.subject}
                onChange={handleInputChange('subject')}
                error={!!formErrors.subject}
                helperText={formErrors.subject}
                sx={{ marginBottom: 3 }}
                placeholder="Brief description of your inquiry"
                disabled={isSubmitting}
              />
              
              <TextField
                fullWidth
                label="Message"
                multiline
                rows={6}
                value={formData.message}
                onChange={handleInputChange('message')}
                error={!!formErrors.message}
                helperText={formErrors.message}
                sx={{ marginBottom: 3 }}
                placeholder="Please provide details about your question or issue..."
                disabled={isSubmitting}
              />
              
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={isSubmitting ? <CircularProgress size={20}/> : <SendIcon />}
                disabled={isSubmitting}
                sx={{
                  backgroundColor: '#29C0BB',
                  borderRadius: '8px',
                  textTransform: 'none',
                  padding: '12px 24px',
                  fontSize: '16px'
                }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              </Button>
            </Box>
            <div style={{ marginTop: '24px', textAlign: 'center' }}>
              <Typography variant="body2" sx={{ marginBottom: 3, color: '#666' }}>
                Or reach out to us directly at{' '}
                <a 
                  href={`mailto:info@tikcare.co?subject=${encodeURIComponent(`${PRODUCT_NAME} Inquiry`)}`}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  info@tikcare.co
                </a>
              </Typography>
            </div>
          </Paper>
        </Grid>

        {/* Documentation Section */}
        {/* <Grid size={12}>
          <Paper 
            elevation={2} 
            sx={{ 
              backgroundColor: '#f8f9fa', 
              borderRadius: '12px', 
              padding: '32px',
              textAlign: 'center',
              height: 'fit-content',
            }}
          >
            <DocumentationIcon sx={{ fontSize: 48, color: '#1976d2', marginBottom: 2 }} />
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Documentation
            </Typography>
            <Typography variant="body2" sx={{ marginBottom: 3, color: '#666' }}>
              Access comprehensive guides and system documentation.
            </Typography>
            <Button
              variant="outlined"
              size="large"
              onClick={() => setDocDialogOpen(true)}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                padding: '12px 24px',
                fontSize: '16px',
                borderWidth: '2px',
                '&:hover': {
                  borderWidth: '2px'
                }
              }}
            >
              View Documentation
            </Button>
          </Paper>
        </Grid> */}
      </Grid>

      {/* Documentation Dialog */}
      <Dialog
        open={docDialogOpen}
        onClose={() => setDocDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#f5f5f5', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DocumentationIcon />
          {PRODUCT_NAME} Documentation
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          <Box sx={{ 
            whiteSpace: 'pre-line', 
            lineHeight: 1.6,
            '& h1': { fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '16px', marginTop: '16px' },
            '& h2': { fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '12px', marginTop: '20px' },
            '& h3': { fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '8px', marginTop: '16px' },
          }}>
            {documentationContent.split('\n').map((line, index) => {
              if (line.startsWith('# ')) {
                return <Typography key={index} variant="h4" component="h1" sx={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '16px', marginTop: '16px' }}>{line.substring(2)}</Typography>;
              } else if (line.startsWith('## ')) {
                return <Typography key={index} variant="h5" component="h2" sx={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '12px', marginTop: '20px' }}>{line.substring(3)}</Typography>;
              } else if (line.startsWith('### ')) {
                return <Typography key={index} variant="h6" component="h3" sx={{ fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '8px', marginTop: '16px' }}>{line.substring(4)}</Typography>;
              } else if (line.startsWith('- ')) {
                return <Typography key={index} variant="body2" sx={{ marginLeft: '16px', marginBottom: '4px' }}>• {line.substring(2)}</Typography>;
              } else if (line.match(/^\d+\./)) {
                return <Typography key={index} variant="body2" sx={{ marginLeft: '16px', marginBottom: '4px', fontWeight: 500 }}>{line}</Typography>;
              } else if (line.trim() === '') {
                return <Box key={index} sx={{ height: '8px' }} />;
              } else {
                return <Typography key={index} variant="body2" sx={{ marginBottom: '8px' }}>{line}</Typography>;
              }
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', backgroundColor: '#f5f5f5' }}>
          <Button 
            onClick={() => setDocDialogOpen(false)}
            variant="contained"
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Help;